import type { Translations } from "./types";

export const en: Translations = {
  // Common
  save: "Save",
  cancel: "Cancel",
  delete: "Delete",
  loading: "Loading...",
  back: "Back",
  search: "Search",
  close: "Close",
  confirm: "Confirm",
  edit: "Edit",
  create: "Create",
  copy: "Copy",
  copied: "Copied",
  download: "Download",
  upload: "Upload",
  settings: "Settings",
  profile: "Profile",
  logout: "Log Out",
  login: "Log In",
  signup: "Sign Up",

  // Navigation
  nav_home: "Home",
  nav_explore: "Explore",
  nav_community: "Community",
  nav_messages: "Messages",
  nav_settings: "Settings",
  nav_profile: "Profile",

  // Settings - Appearance
  settings_appearance: "Appearance",
  settings_appearance_desc: "Customize the look and feel of the interface.",
  settings_theme: "Theme",
  settings_theme_desc: "Choose between light and dark themes for the interface.",
  settings_theme_light: "Light",
  settings_theme_light_desc: "Warm parchment",
  settings_theme_dark: "Dark",
  settings_theme_dark_desc: "Dark dungeon",
  settings_theme_system: "System",
  settings_theme_system_desc: "Match device",
  settings_language: "Language",
  settings_language_desc: "Choose your preferred interface language.",
  locale_auto_detect: "Auto (detect)",
  settings_login_bg: "Login Background",
  settings_login_bg_desc: "Set a custom background image for the login and signup page.",
  settings_login_bg_url_placeholder: "https://example.com/background.jpg",
  settings_login_bg_hint: "Paste a URL to a background image. Leave empty to use the default.",

  // Settings - AI
  settings_ai: "AI Provider",
  settings_ai_desc: "Configure AI models, API keys, and web research tools.",
  settings_provider: "Provider",
  settings_provider_desc: "Choose between the built-in Lovable AI or your own Gemini API key.",
  settings_use_own_key: "Use my own Gemini API Key",
  settings_using_own_key: "Using your personal Gemini API key",
  settings_using_lovable: "Using built-in Lovable AI (recommended)",
  settings_lovable_ready: "✨ Lovable AI is ready to use — no setup required! Supports Gemini & GPT models.",
  settings_test_connection: "Test Connection",
  settings_test_key: "Test API Key",
  settings_test_lovable: "Test Lovable AI",
  settings_testing: "Testing...",

  // Settings - Voice
  settings_voice: "Voice",
  settings_voice_desc: "Configure text-to-speech providers and voices.",
  settings_tts_provider: "Text-to-Speech Provider",
  settings_tts_provider_desc: "Choose which TTS engine powers narration voice playback.",
  settings_default_voice: "Default Narrator Voice",
  settings_available_voices: "Available Voices",

  // Settings - Credits
  settings_credits: "Credits",
  settings_credits_desc: "Your wallet, referral program, and credit pricing details.",

  // Settings - Analytics
  settings_analytics: "Analytics",
  settings_analytics_desc: "AI usage metrics and activity log.",

  // Settings - Tools
  settings_tools: "Tools & Diagnostics",
  settings_tools_desc: "Testing panels and diagnostic suites.",

  // Settings tabs
  tab_appearance: "Appearance",
  tab_ai_provider: "AI Provider",
  tab_credits: "Credits",
  tab_voice: "Voice",
  tab_music_library: "Music Library",
  tab_analytics: "Analytics",
  tab_error_logs: "Error Logs",
  tab_tools: "Tools",

  // Profile
  profile_title: "Profile",
  profile_display_name: "Display Name",
  profile_email: "Email",
  profile_bio: "Bio",
  profile_signature: "Forum Signature",
  profile_public: "Public Profile",
  profile_avatar: "Avatar",
  profile_change_password: "Change Password",
  profile_current_password: "Current Password",
  profile_new_password: "New Password",
  profile_confirm_password: "Confirm New Password",
  profile_update: "Update Profile",
  profile_friends: "Friends",
  profile_delete_account: "Delete Account",
  profile_delete_confirm: "Are you sure you want to delete your account? This action cannot be undone.",

  // Dashboard
  dashboard_worlds: "Worlds",
  dashboard_adventures: "Adventures",
  dashboard_create_world: "Create World",
  dashboard_no_worlds: "No worlds yet",
  dashboard_no_adventures: "No adventures yet",

  // Play
  play_your_turn: "Your Turn",
  play_send: "Send",
  play_type_action: "What do you do?",

  // World Builder
  world_builder: "World Builder",
  world_entities: "Entities",
  world_plots: "Plots",
  world_mechanics: "Mechanics",
  world_scenarios: "Scenarios",

  // Misc
  no_results: "No results found",
  error_occurred: "An error occurred",
  success: "Success",

  // ── Auth ──
  auth_sign_in: "Sign In",
  auth_sign_up: "Join the Waitlist",
  auth_signing_in: "Signing in…",
  auth_signing_up: "Joining waitlist…",
  auth_sign_in_subtitle: "Sign in to continue your adventure",
  auth_sign_up_subtitle: "Join the closed-beta waitlist — we'll email you when it's your turn",
  auth_reset_password: "Reset Password",
  auth_reset_subtitle: "Enter your email and we'll send you a reset link",
  auth_forgot_password: "Forgot password?",
  auth_send_reset_link: "Send Reset Link",
  auth_sending: "Sending…",
  auth_no_account: "No account yet? Join the waitlist",
  auth_has_account: "Already have an account? Sign in",
  auth_back_to_sign_in: "Back to sign in",
  auth_email: "Email",
  auth_password: "Password",
  auth_display_name: "Display Name",
  auth_email_placeholder: "you@example.com",
  auth_password_placeholder: "••••••••",
  auth_password_signup_placeholder: "At least 6 characters",
  auth_password_min: "Minimum 6 characters",
  auth_name_placeholder: "Your name",
  auth_referral_bonus: "Referred! Your application will be prioritised",
  auth_daily_bonus: "Daily bonus: +{credits} credits!",
  auth_welcome_back: "Welcome back!",
  auth_check_email: "Check your email to confirm your waitlist spot!",
  auth_reset_sent: "Password reset link sent! Check your inbox.",
  auth_enter_email: "Please enter your email address.",

  // ── AppHeader / Nav ──
  nav_worlds: "Worlds",
  nav_about: "About",
  nav_roadmap: "Roadmap",
  nav_pricing: "Pricing",
  nav_contact: "Contact",
  nav_forums: "Forums",
  nav_users: "Users",
  nav_sign_in_up: "Sign in / Join Waitlist",
  nav_guest_browsing: "You are browsing as a guest",
  nav_set_display_name: "Set Display Name",
  nav_user_management: "User Management",
  nav_sign_out: "Sign out",
  nav_active_tasks: "Active Tasks",
  nav_clear: "Clear",
  gh_open_menu: "Open menu",
  gh_menu: "Menu",
  gh_account: "Account",
  gh_credits: "Credits",
  nav_your_credits: "Your credits",
  nav_no_credits: "No credits — all AI features disabled",
  nav_low_credits: "Low credits — world building disabled",
  nav_transfers_disabled: "Credits below 100 — transfers disabled",

  // Task phases
  task_searching: "Searching web",
  task_writing_brief: "Writing brief",
  task_generating: "Generating image",
  task_complete: "Complete",
  task_failed: "Failed",

  // ── Index / Dashboard (extended) ──
  idx_tagline: "Build worlds with AI, then play through them as text adventures.",
  idx_my_worlds: "My Worlds",
  idx_my_adventures: "My Adventures",
  idx_public_worlds: "Public Worlds",
  idx_trash: "Trash",
  idx_new_world: "New World",
  idx_create_new_world: "Create New World",
  idx_manual: "Manual",
  idx_manual_desc: "Title & description",
  idx_ai_prompt: "AI Prompt",
  idx_ai_prompt_desc: "Describe & let AI build",
  idx_world_name_placeholder: "World name (leave blank to auto-generate)...",
  idx_world_desc_placeholder: "Short description (leave blank to auto-generate)...",
  idx_world_prompt_placeholder: "Describe the world you want to create... e.g. 'A dark fantasy world inspired by Bloodborne, with cosmic horror elements and interconnected city districts'",
  idx_reference_files: "Reference Files (optional)",
  idx_terms_title: "⚠️ Terms & Disclaimer",
  idx_terms_content_1: "By creating a world you confirm that you own or have the rights to all uploaded media and content. You accept full responsibility for any materials you upload.",
  idx_terms_content_2: "AI-generated content (text, images, scenarios) is provided as-is with no guarantees of accuracy, appropriateness, or fitness for any purpose. We assume no liability for AI-generated outputs.",
  idx_create_with_ai: "Create with AI",
  idx_create_and_build: "Create & Start Building",
  idx_no_worlds_hint: "No worlds yet. Create your first world to get started!",
  idx_your_worlds: "Your Worlds",
  idx_shared_with_you: "Shared With You",
  idx_new_game: "New Game",
  idx_join_game: "Join Game",
  idx_join_game_title: "Join a Game",
  idx_join_game_desc: "Enter an invite code or paste an invite link to join a multiplayer adventure.",
  idx_invite_code_placeholder: "Invite code...",
  idx_join: "Join",
  idx_joining_adventure: "Joining adventure...",
  idx_select_world: "Select a World",
  idx_choose_world: "Choose a world to play in...",
  idx_no_worlds_available: "No worlds available. Build a world first!",
  idx_select_or_new_adventure: "Continue an existing adventure or start a new one.",
  idx_or: "or",
  idx_your_turn: "Your Turn",
  idx_adventures: "Adventures",
  idx_no_adventures_hint: "No adventures yet. Start a new game or join one!",
  idx_trashed_worlds: "Trashed Worlds",
  idx_trash_desc: "Deleted worlds are kept here. Restore them or permanently delete them.",
  idx_trash_empty: "Trash is empty",
  idx_restore: "Restore",
  idx_delete_forever: "Delete Forever",
  idx_delete_forever_title: "Permanently delete this world?",
  idx_delete_forever_desc: "and all its entities, plots, agents, and media will be permanently deleted. This cannot be undone.",
  idx_no_public_worlds: "No public worlds available yet.",
  idx_solo: "Solo",
  idx_joined: "Joined",
  idx_yours: "Yours",
  idx_updated: "Updated",
  idx_deleted: "Deleted",
  idx_turn: "Turn",
  idx_drop_files: "Drop files here or use Upload button",
  idx_start_new_game: "Start New Game",

  // ── Profile (extended) ──
  profile_account_settings: "Account Settings",
  profile_account_settings_desc: "Manage your profile and security",
  profile_info: "Profile Information",
  profile_info_desc: "Update your personal details",
  profile_achievements: "Achievements",
  profile_avatar_hint: "Upload an image or paste a direct image URL.",
  profile_name_placeholder: "Enter your display name",
  profile_name_hint: "This is how other players will see you",
  profile_email_readonly: "Email cannot be changed",
  profile_bio_placeholder: "Tell others about yourself...",
  profile_avatar_url_placeholder: "https://example.com/avatar.jpg",
  profile_signature_placeholder: "This appears below your forum posts...",
  profile_signature_hint: "Up to 240 characters.",
  profile_public_hint: "Allow others to see your profile",
  profile_tab_byok: "BYOK",
  profile_secret_mode: "Secret Mode",
  profile_secret_mode_desc: "Hide yourself from player search. Existing friends can still see you.",
  profile_save_changes: "Save Changes",
  profile_updating: "Updating...",
  profile_password_desc: "Update your account password",
  profile_password_changing: "Changing...",
  profile_account_actions: "Account Actions",
  profile_account_actions_desc: "Manage your account",
  profile_logout_confirm: "Are you sure?",
  profile_logout_confirm_desc: "You will be logged out of your account.",

  // ── Play ──
  play_what_do_you_do: "What do you do?",
  play_waiting_turn: "Waiting for other player's turn...",
  play_debug_log: "Download debug log",
  play_adventure_state: "Adventure State",

  // ── World Builder (extended) ──
  wb_title: "World Builder",
  wb_empty_desc: "Describe a world concept and I'll create entities, locations, characters, and storylines.",
  wb_describe_placeholder: "Describe your world...",
  wb_describe_placeholder_full: "Enter to send · Shift+Enter for new line · Ctrl+Enter to queue",
  wb_shuffle: "Shuffle suggestions",
  wb_show_older: "Show older",
  wb_queue: "Queue",
  wb_queue_paused: "Paused",
  wb_send_queue: "Send Queue",
  wb_resume: "Resume",
  wb_pause: "Pause",
  wb_unpause: "Unpause",
  wb_clear: "Clear",
  wb_stop: "Stop",
  wb_retry: "Resume / Retry",
  wb_unsaved: "Unsaved",
  wb_unsaved_title: "Unsaved Changes",
  wb_unsaved_desc: "You have unsaved changes. Do you want to save before leaving?",
  wb_leave_without_saving: "Leave without saving",
  wb_save_and_leave: "Save & Leave",
  wb_read_only: "Read-only",
  wb_update: "Update",
  wb_full_access: "Full access",
  wb_debug_dump: "Download debug dump",
  wb_panel_title: "World Builder Panel",

  // ── World Builder Panel (CreationPanel) ──
  wb_entities: "entities",
  wb_save: "Save",
  wb_saved: "Saved",
  wb_play: "Play",
  wb_play_adventure: "Play Adventure",
  wb_ui_version_label: "Builder UI",
  wb_ui_version_desc: "Choose which World Builder interface to use.",
  wb_ui_version_v1: "V1",
  wb_ui_version_v2: "V2",
  flow_version_label: "Flow Engine",
  flow_version_desc: "V2 uses the Agents SDK pipeline (experimental).",
  flow_version_v1: "V1 — Classic",
  flow_version_v2: "V2 — SDK",
  wb_v2_canvas_hint: "V2 canvas — work in progress",
  wb_v2_empty: "No entities yet — ask the agent to build your world",
  wb_v2_actions: "Actions",
  wb_v2_action_add: "Add",
  wb_v2_action_generate: "Generate",
  wb_v2_action_layout: "Layout",
  wb_v2_action_settings: "Settings",
  wb_v2_coming_soon: "Coming soon",
  wb_loading_adventures: "Loading adventures...",
  wb_continue_playing: "Continue Playing",
  wb_or_start_new: "or start new",
  wb_adventure_name: "Adventure name...",
  wb_multiplayer: "Multiplayer",
  wb_multiplayer_desc: "Invite others to join",
  wb_turn_mode: "Turn Mode",
  wb_free_form: "Free-form",
  wb_round_robin: "Round Robin",
  wb_create_mp: "Create Multiplayer Adventure",
  wb_begin_new: "Begin New Adventure",
  wb_share: "Share",
  wb_world_banner: "World Banner",
  wb_trash: "Trash",
  wb_section_build: "Build",
  wb_section_statistics: "Statistics",
  wb_section_feedback: "Feedback",
  wb_section_publishing: "Publishing",
  wb_group_general: "General",
  wb_group_data: "Data",
  wb_group_media: "Media",
  wb_group_engine: "Engine",
  wb_group_agents: "Agents",
  wb_tab_world_gen: "World Generation",
  wb_tab_checklist: "Checklist",
  wb_tab_locations: "Locations",
  wb_tab_map: "Map",
  wb_tab_entities: "Entities",
  wb_tab_plot: "Plot",
  wb_tab_context: "Context",
  wb_tab_media: "Media Library",
  wb_tab_voice: "Voice Settings",
  wb_tab_music: "Music",
  wb_tab_mechanics: "Mechanics",
  wb_tab_scenarios: "Scenarios",
  wb_tab_players: "Players",
  wb_tab_agents: "Agent Library",
  wb_tab_chats: "Chats",
  wb_tab_about: "About",
  wb_tab_privacy: "Privacy & Sharing",
  wb_tab_actions: "Actions",
  wb_tab_design_issues: "Game Feedback",
  wb_quick_create: "Create new",
  wb_describe_create: "Describe what to create...",
  wb_send: "Send",
  wb_tab_promotion: "Promotion",
  wb_section_instances: "Active Games",
  wb_group_general_desc: "World identity and settings",
  wb_group_data_desc: "World content and lore",
  wb_group_media_desc: "Visual and audio assets",
  wb_group_engine_desc: "Rules, scenarios, and players",
  wb_group_agents_desc: "AI agents and conversations",
  wb_quick_new_entity: "Create a new interesting entity that fits the world",
  wb_quick_new_plot: "Create a new plot hook or story arc for this world",
  wb_quick_new_agent: "Create a new AI agent for this world",
  wb_quick_new_location: "Create a new location for this world",

  // ── World AI Provider ──
  ai_title: "AI Provider",
  ai_desc: "Configure which AI provider and model this world uses. Each world can use a different provider.",
  ai_membership_required: "Membership Required",
  ai_membership_desc: "Per-world AI provider configuration requires an active membership with BYOK (Bring Your Own Key) set up. Go to your Profile → API Keys to set up your accounts.",
  ai_use_custom: "Use Custom AI for this World",
  ai_use_custom_desc: "Override the default platform AI with your own BYOK provider for all AI calls in this world.",
  ai_no_accounts: "No Text Accounts",
  ai_no_accounts_desc: "You haven't set up any BYOK text accounts yet. Go to Profile → API Keys → Text tab to add one.",
  ai_account_label: "AI Account",
  ai_select_account: "Select an account",
  ai_default_account: "Default BYOK Account",
  ai_account_hint: "Choose which BYOK text account to use for AI calls in this world. \"Default\" uses whichever account is marked as default in your profile.",
  ai_save_config: "Save AI Configuration",
  ai_config_saved: "AI configuration saved",
  ai_save_failed: "Failed to save: ",
  ai_verified: "verified",
  ai_default_badge: "default",

  // ── Plot Panel ──
  plot_cat_main_quest: "Main Quest",
  plot_cat_side_quest: "Side Quest",
  plot_cat_plot_hook: "Plot Hook",
  plot_cat_conflict: "Conflict",
  plot_cat_key_moment: "Key Moment",
  plot_cat_event: "Event",
  plot_cat_development: "Development",
  plot_cat_lore: "Lore",
  plot_cat_completion: "Completion",
  plot_cat_note: "Note",
  plot_tab_quests: "Quests",
  plot_tab_quests_desc: "Main and side quests with objectives and scenario links.",
  plot_tab_story: "Story",
  plot_tab_story_desc: "Key moments, events, plot hooks, conflicts, and lore.",
  plot_tab_conditions: "Conditions",
  plot_tab_conditions_desc: "Victory, defeat, and ending conditions.",
  plot_relevance_critical: "Critical",
  plot_relevance_high: "High",
  plot_relevance_medium: "Medium",
  plot_relevance_low: "Low",
  plot_relevance_background: "Background",
  plot_objectives: "Objectives",
  plot_add_objective: "Add objective...",
  plot_entry_title: "Entry title...",
  plot_add_entry: "Add Entry",
  plot_no_quests: "No quests yet",
  plot_no_story: "No story entries yet",
  plot_no_conditions: "No completion conditions yet",
  plot_add_first: "Add First Entry",
  plot_title_placeholder: "Title...",
  plot_describe_placeholder: "Describe this plot element...",
  plot_category: "Category",
  plot_relevance: "Relevance",
  plot_linked_scenario: "Linked Scenario",
  plot_none: "None",
  plot_no_description: "No description yet",
  plot_all_scenarios: "All Scenarios",
  plot_aria_filter_scenario: "Filter by scenario",
  plot_aria_new_entry_category: "New entry category",
  plot_aria_edit_category: "Edit category",
  plot_aria_linked_scenario: "Linked scenario",
  plot_merge: "Merge",
  plot_selected: "selected",
  plot_select_min_2: "Select at least 2 entries to merge",
  plot_merge_failed: "Merge failed",
  plot_deleted: "Entry deleted",
  plot_delete_failed: "Failed to delete",
  plot_save_failed: "Failed to save",
  plot_add_failed: "Failed to add entry",

  // ── Design Issues Panel ──
  di_loading: "Loading design feedback…",
  di_no_issues: "No design issues detected",
  di_no_issues_desc: "Issues are collected during gameplay and surfaced here for you to fix.",
  di_title: "Design Issues",
  di_open: "open",
  di_desc: "Issues detected during gameplay that may need your attention in the world editor.",
  di_cat_entity: "Entity",
  di_cat_mechanic: "Mechanic",
  di_cat_prompt: "Prompt",
  di_cat_tool: "Tool",
  di_cat_content: "Content",
  di_dismiss: "Dismiss",
  di_occurrences: "occurrences",
  di_first_seen_turn: "First seen: turn",
  di_source_ai: "AI detected",
  di_source_rule: "Rule detected",

  // ── Privacy Section (extras) ──
  priv_confidential: "Confidential Mode",
  priv_confidential_desc: "Restrict this world to invited users only. Platform AI is disabled — only your external AI provider will be used.",
  priv_confidential_active: "Confidential Mode is active.",
  priv_confidential_active_desc: "This world is hidden from public discovery, restricted to invited users, and uses only your configured external AI provider (BYOK / Custom AI). Public sharing, publishing, and cloning are disabled.",
  priv_locked_by_confidential: "Locked by Confidential Mode",
  priv_share_link: "Share Link",
  priv_share_link_desc: "Share this link to let anyone preview your world and start playing.",
  priv_share_copied: "Share link copied!",

  // ── Generation Section (extras) ──
  gen_conservative: "Conservative",
  gen_balanced: "Balanced",
  gen_aggressive: "Aggressive",
  gen_conservative_desc: "Minimal auto-generation. Only creates what the world strictly requires.",
  gen_balanced_desc: "Moderate generation. Creates entities and lore as needed.",
  gen_aggressive_desc: "Maximum world-building. Generates entities, lore, images prolifically.",
  gen_conservative_detail: "New entities only when explicitly introduced. No scene images, no speculative lore.",
  gen_balanced_detail: "Auto-creates NPCs & locations on mention. Occasional scene images.",
  gen_aggressive_detail: "Proactive NPC/location/plot creation. Auto scene images. Codex auto-maintenance.",

  // ── Context Panel ──
  ctx_description: "Description",
  ctx_click_to_edit: "Click to edit",
  ctx_click_to_add: "Click to add a description, or let the AI generate one.",
  ctx_media_generation: "Media Generation",
  ctx_allow_ingame: "Allow in-game generation",
  ctx_allow_ingame_desc: "When enabled, players can generate and regenerate character portraits during gameplay. When disabled, media generation is restricted to the World Builder.",
  ctx_media_enabled: "In-game media generation enabled",
  ctx_media_disabled: "In-game media generation disabled",
  ctx_update_failed: "Failed to update setting",

  // ── Scenarios Panel ──
  scn_desc: "Define starting scenarios — templates that shape how adventures begin.",
  scn_new: "New Scenario",
  scn_no_scenarios: "No scenarios yet.",
  scn_no_scenarios_desc: "Create scenarios to give players structured starting points. Without scenarios, adventures start free-form.",
  scn_create_first: "Create First Scenario",
  scn_needs_title: "Scenario needs a title",
  scn_updated: "Scenario updated",
  scn_created: "Scenario created",
  scn_default_set: "Default scenario set",
  scn_deleted: "Scenario moved to trash",
  scn_delete_failed: "Failed to delete scenario",
  scn_difficulty_easy: "Easy",
  scn_difficulty_normal: "Normal",
  scn_difficulty_hard: "Hard",
  scn_difficulty_brutal: "Brutal",
  scn_difficulty_adaptive: "Adaptive",
  scn_img_saved: "Scenario image saved",
  scn_img_save_failed: "Failed to save image",
  scn_img_removed: "Scenario image removed",
  scn_img_remove_failed: "Failed to remove image",

  // ── About Section (editor) ──
  about_preview_page: "Preview Page",
  about_click_change_icon: "Click to change icon",
  about_click_set: "Click to set",
  about_world_name_hint: "The display name shown to players and on the public page.",
  about_world_id_copied: "World ID copied",
  about_anonymous: "Anonymous",

  // ── Actions Section (editor) ──
  act_world_deleted: "World deleted",
  act_delete_failed: "Failed to delete",
  act_clone_placeholder: "My Cloned World",
  act_clone_failed: "Clone failed",
  act_cloned: "World cloned successfully!",

  // ── Gameplay panels ──
  play_auto: "Auto",
  play_manual: "Manual",
  play_no_response: "⚠ No response received",
  play_send_again: "Send again",
  play_may_not_received: "⚠ May not have been received",
  play_you: "You",
  play_turn: "Turn",
  play_active: "Active",
  play_switch: "Switch",
  play_no_pov: "No POV characters registered yet. The narrator will add them as the story progresses.",
  play_no_party: "No party members",
  play_party: "Party",
  play_waiting_char: "Waiting for character creation...",
  play_inner_voice: "Inner Voice",
  play_thoughts_log: "Log",
  play_observing: "is observing quietly…",
  play_awaiting_thoughts: "Awaiting thoughts…",

  // ── Decision Journal ──
  play_decision_journal: "Decision Journal",
  play_pending_consequences: "pending consequences",
  play_choices_pending: "Some of your choices haven't fully played out yet.",
  play_immediate: "Immediate",
  play_ripple_effects: "Ripple Effects",

  // ── Encounter / Events ──
  play_no_events: "No Events Yet",
  play_no_events_desc: "Events, encounters, and world news will appear here as the story unfolds.",
  play_events_world: "Events & World",
  play_active_events: "Active Events",
  play_pending_ripples: "Pending Ripples",

  // ── Lore Codex ──
  play_codex_characters: "Characters",
  play_codex_locations: "Locations",
  play_codex_quests: "Quests & Goals",
  play_codex_factions: "Factions & Orgs",
  play_codex_items: "Items & Artifacts",
  play_codex_lore: "Lore & Knowledge",

  // ── Research Panel ──
  play_research_tree: "Research Tree",
  play_research_in_progress: "In Progress",
  play_research_queued: "Queued",
  play_research_completed: "Completed",
  play_research_none: "No research started yet",
  play_research_ask: "Ask about available research topics",

  // ── Operations Panel ──
  play_ops_overview: "Overview",
  play_ops_territory: "Territory",
  play_ops_workforce: "Workforce",
  play_ops_resources: "Resources",
  play_ops_unnamed_org: "Unnamed Organization",

  // ── Org tiers/types/styles ──
  play_tier_street: "Street Level",
  play_tier_established: "Established",
  play_tier_major: "Major Player",
  play_tier_dominant: "Dominant Force",
  play_tier_undisputed: "Undisputed Power",
  play_org_criminal: "Criminal Syndicate",
  play_org_intelligence: "Intelligence Cell",
  play_org_corporate: "Corporate Empire",
  play_org_military: "Military Command",
  play_org_custom: "Custom",
  play_style_hierarchical: "Hierarchical",
  play_style_cell: "Cell-based",
  play_style_feudal: "Feudal",

  // ── Item rarity ──
  play_rarity_common: "Common",
  play_rarity_uncommon: "Uncommon",
  play_rarity_rare: "Rare",
  play_rarity_epic: "Epic",
  play_rarity_legendary: "Legendary",

  // ── Dice Tray ──
  play_quick_rolls: "Quick Rolls",
  play_dice_hint: "The narrator resolves dice, modifiers & DC",

  // ── Location detail ──
  play_known_details: "Known Details",
  play_travel_to: "Travel to",

  // ── Codex extra ──
  play_codex_empty: "Codex Empty",
  play_codex_empty_desc: "Discoveries will appear here as you explore the world — people you meet, places you visit, and lore you uncover.",
  play_codex_title: "Codex",
  play_codex_search: "Search codex…",
  play_codex_other: "Other",

  // ── Encounter extra ──
  play_enc_all: "All",
  play_enc_encounters: "Encounters",
  play_enc_news: "News",
  play_enc_ripples: "Ripples",
  play_enc_no_encounters: "No encounters yet",
  play_enc_no_news: "No world news yet",
  play_enc_since_event: "since last event",
  play_enc_since_news: "since news",

  // ── Research extra ──
  play_research_tier: "Tier",
  play_research_turn_left: "left",

  // ── Org / Operations shared ──
  play_org_unnamed: "Unnamed Organization",
  play_org_treasury: "Treasury",
  play_org_income_turn: "Income/Turn",
  play_org_tier: "Tier",
  play_org_morale: "Morale",
  play_org_heat: "Heat",
  play_org_territories: "Territories",
  play_org_personnel: "Personnel",
  play_org_resources: "Resources",
  play_org_no_territory: "No territory data yet. Claim locations to build your domain.",
  play_org_no_personnel: "No personnel recruited yet.",
  play_org_no_resources: "No resources tracked yet. Resources appear as the story introduces them.",
  play_org_controlled: "Controlled",
  play_org_contested: "Contested",
  play_org_rival_factions: "Rival Factions",
  play_org_stockpile: "Stockpile",
  play_org_production: "Production",
  play_org_upkeep: "Upkeep",
  play_org_controlled_loc: "Controlled Locations",
  play_org_no_territory_ctrl: "No territories controlled yet",

  // ── Item card ──
  play_item_equipped: "Equipped",
  play_item_modifiers: "Modifiers",
  play_item_when_equipped: "(when equipped)",
  play_item_effects: "Effects",
  play_item_durability: "Durability",
  play_item_charges: "Charges",
  play_item_weight: "Weight",
  play_item_cursed: "Cursed",
  play_item_quest: "Quest Item",

  // ── Recent thoughts ──
  play_recent_thoughts: "Recent Thoughts",

  // ── PlayChatInput extra ──
  play_autoscroll_on: "Auto-scroll ON",
  play_autoscroll_off: "Auto-scroll OFF",
  play_debug_downloads: "Debug Downloads",
  play_conversation_log: "Conversation Log",
  play_game_snapshot: "Game Snapshot",

  // ── Locations Panel (editor) ──
  loc_open_map_editor: "Open Map Editor",
  loc_maps: "Maps",
  loc_no_maps: "No maps created yet",
  loc_new_map: "New Map",
  loc_map_editor: "Map Editor",

  // ── Play widgets ──
  widget_rest_title: "Rest & Recovery",
  widget_rest_hit_dice: "Hit Dice",
  widget_rest_last_long: "Last long rest",
  widget_rest_today: "Today",
  widget_rest_days_ago: "d ago",
  widget_rest_short: "Short Rest",
  widget_rest_long: "Long Rest",
  widget_combat_death_saves: "Death Saves",
  widget_combat_attack: "Attack",
  widget_combat_defend: "Defend",
  widget_combat_flee: "Flee",
  widget_combat_special: "Special",
  widget_combat_attack_desc: "Strike at your target",
  widget_combat_defend_desc: "Brace for impact",
  widget_combat_flee_desc: "Try to escape",
  widget_combat_special_desc: "Use an ability",
  widget_conditions_title: "Conditions",
  widget_conditions_none: "No active conditions",
  widget_conditions_exhaustion: "Exhaustion",
  widget_conditions_concentration: "Concentration",
  widget_conditions_turns_left: "turns left",

  // ── About page ──
  about_hero_title_1: "Every Story Deserves",
  about_hero_title_2: "a Game Engine.",
  about_hero_desc: "Adventure Venture is an AI game master platform where you build custom worlds, play with friends or solo, NPCs remember everything across sessions, and bring-your-own-key pricing means you control the costs. Your choices shape what comes next — even the terrible ones.",
  about_hero_desc_em: "Especially the terrible ones.",
  about_hero_cta_dashboard: "Go to Dashboard",
  about_hero_cta_start: "Join the Waitlist",
  about_hero_cta_browse: "Browse Worlds",
  about_bottom_title: "Your World Is Waiting.",
  about_bottom_desc: "Build something absurd. Build something epic. Build something that makes absolutely no sense and then watch the AI try to make it work. We believe in you. Probably.",
  about_badge_worldbuilding: "World Building",
  about_badge_gamemaster: "AI Game Master",
  about_badge_survival: "Survival & Combat",
  about_badge_npcs: "Living NPCs",
  about_badge_timetravel: "Any Genre. Any Setting.",
  about_badge_multiplayer: "Multiplayer",
  about_sub_worldbuilding: "The World Codex",
  about_sub_gamemaster: "The Story Orchestra",
  about_sub_survival: "Mechanics That Bite Back",
  about_sub_npcs: "The Consequence Engine",
  about_sub_timetravel: "Unlimited Possibility Space",
  about_sub_multiplayer: "Co-op Chaos",
  about_title_worldbuilding: "Build Entire Universes.\nFrom Your Couch.",
  about_title_gamemaster: "A GM Who Never\nCancels Game Night.",
  about_title_survival: "Die Memorably.\nRepeatedly.",
  about_title_npcs: "NPCs That Hold\nGrudges.",
  about_title_timetravel: "Dinosaurs Meet\nSpace Marines.",
  about_title_multiplayer: "Ruin Friendships.\nTogether.",
  about_desc_worldbuilding_1: "Imagine if someone handed you a spreadsheet and said \"fill this out, it's a universe.\" That's basically what we did, except the spreadsheet is sentient and does most of the work for you.",
  about_desc_worldbuilding_2: "Describe your world in plain language — or upload existing lore documents — and watch as the AI populates continents, factions, NPCs with suspicious backstories, and that one tavern that every fantasy world apparently requires by law.",
  about_desc_worldbuilding_3: "Every entity you create is interconnected. Change the king's name, and suddenly the rebellion needs new protest signs. The Codex tracks relationships, hierarchies, and grudges so you don't have to.",
  about_desc_gamemaster_1: "Our AI Game Master is less of a robot reading from a script and more of a dramatically unhinged theater director who happens to control reality. It orchestrates multiple AI agents — narrator, NPCs, combat referee, lore keeper — all arguing behind the scenes about what should happen next.",
  about_desc_gamemaster_2: "The Difficulty Director quietly adjusts challenge ratings based on your performance. Stomping everything? Enjoy this sudden ambush. Getting destroyed? That conveniently placed healing fountain was always there, definitely.",
  about_desc_gamemaster_3: "The Pacing Engine tracks dramatic tension like a soap opera writer with a spreadsheet. Quiet moments build to crescendos. Victories feel earned. Defeats feel dramatic. That's not a bug — that's narrative structure.",
  about_desc_survival_1: "D&D-style combat with actual dice rolls, initiative order, and that beautiful moment when you roll a natural 1 while trying to impress the dragon. The combat system handles hit points, conditions, saving throws, and the existential dread of running out of spell slots.",
  about_desc_survival_2: "Survival mechanics track hunger, thirst, temperature, and exhaustion — because nothing says \"epic adventure\" like your barbarian catching a cold. Craft gear, manage resources, and argue with the AI about whether eating suspicious mushrooms counts as a meal.",
  about_desc_survival_3: "An economy system where gold actually matters. Buy equipment, bribe guards, invest in real estate in a fictional kingdom. Your quest journal tracks everything you've accomplished, everything you've failed at, and everything you're actively pretending didn't happen.",
  about_desc_npcs_1: "Every NPC is powered by its own AI agent with memories, opinions, and an alarming capacity for passive-aggression. Steal from a shopkeeper? They remember. Help a farmer? They'll name a cow after you. Betray the guild leader? Good luck finding anyone who'll sell you potions.",
  about_desc_npcs_2: "The Reputation system tracks how every faction feels about you, and they gossip. Oh, do they gossip. Your standing with the Thieves' Guild affects how the Royal Guard treats you, which affects whether the Merchants' Association will give you a discount, which affects whether you can afford that sword you need to fight the thing the Thieves' Guild wants you to fight.",
  about_desc_npcs_3: "A morality system that doesn't judge — it just takes notes. Your choices shift your character's alignment naturally through actions, not menus. The world reacts accordingly. Saints get reverence. Villains get feared. The morally ambiguous get invited to the most interesting parties.",
  about_desc_timetravel_1: "Medieval fantasy is great and all, but what if your knight had to negotiate a trade deal with a cyberpunk corporation? What if your noir detective stumbled into a fairy ring? What if your space captain crash-landed on a world that runs on magic instead of physics?",
  about_desc_timetravel_2: "The engine doesn't care about genre boundaries. Build a Victorian steampunk mystery. A post-apocalyptic cooking show. A political thriller set in a dwarven parliament. Use the built-in research tools to import lore from any source with detail that would make the original writers nervous.",
  about_desc_timetravel_3: "Upload source material — novels, wikis, game guides — and the system absorbs them into the Codex. Your world's lore becomes the AI's lore. It'll reference obscure details from page 47 of your uploaded worldbook like it actually read the thing. Because it did.",
  about_desc_multiplayer_1: "Invite friends to your adventure and watch as your carefully planned heist falls apart because someone decided to pet the guard dog. Multiplayer supports round-robin turns, free-form chaos, and initiative-based combat — because some arguments need structure.",
  about_desc_multiplayer_2: "Each player gets their own character sheet, inventory, and growing list of questionable decisions. The AI tracks everyone's individual reputation, quest progress, and that one player who keeps trying to seduce every NPC regardless of species.",
  about_desc_multiplayer_3: "Share worlds with collaborators who can help build the lore, or publish them for the community to explore. Your Victorian murder mystery could become someone else's comedic romp. That's the beauty of chaos — it's collaborative.",

  // ── BYOK section keys ──
  about_badge_byok: "Bring Your Own Key",
  about_sub_byok: "Your Keys, Your Rules",
  about_title_byok: "Unlimited Play.\nYour API Keys.",
  about_desc_byok_1: "Got a Google Gemini API key lying around? An ElevenLabs account? Good news — plug them in and stop watching your credits drain like a bathtub with no stopper.",
  about_desc_byok_2: "Text generation, voice synthesis, web search — bring your own key for any of these, and those services run on your dime, not your credits. Wiki and Fandom searches still cost platform credits because someone has to pay for those servers, and it's not going to be us. Actually, it is us. That's why they cost credits.",
  about_desc_byok_3: "BYOK users also unlock per-agent model selection — want your villain NPC running on Gemini Pro while the shopkeeper gets Flash Lite? Your key, your call. Choose the brains each character deserves.",

  // ── About FAQ section ──
  about_faq_title: "Frequently Asked Questions",
  about_faq_q1: "What makes Adventure Venture different from AI Dungeon?",
  about_faq_a1: "The main difference is architectural: Adventure Venture runs multiple AI agents simultaneously — a narrator, individual NPC agents, a combat referee, a lore keeper — rather than a single chatbot pretending to be all of them. NPCs have persistent memory across sessions (not just within one conversation), and there are actual game mechanics: dice rolls, survival systems, economy, territory control. AI Dungeon generates stories; Adventure Venture runs a game engine.",
  about_faq_q2: "Do AI NPCs actually remember things between sessions?",
  about_faq_a2: "Yes — this is the feature that surprised beta players most. Every NPC stores memories of your interactions in a persistent database. They remember the promise you broke, the favor you did, the alliance you accidentally stumbled into. Come back three weeks later and the innkeeper still knows you didn't pay your tab. Most AI roleplay platforms reset between sessions; Adventure Venture doesn't.",
  about_faq_q3: "What is BYOK pricing in an AI RPG?",
  about_faq_a3: "BYOK stands for Bring Your Own Key. Instead of Adventure Venture buying AI compute and marking it up, you connect your own API key from OpenAI, Google Gemini, or Anthropic Claude directly. You pay your AI provider at their standard rates — no subscription required for BYOK users. For regular players, this is often significantly cheaper than any platform subscription, with no lock-in.",
  about_faq_q4: "Can I play Adventure Venture with friends?",
  about_faq_a4: "Yes. Adventure Venture supports real-time multiplayer — invite friends to the same world and adventure together with a shared AI Game Master that tracks everyone's history, inventory, and reputation separately. It's one of the few AI RPG platforms that can genuinely replace a human Dungeon Master for a full group.",
  about_faq_q5: "How do I join the closed beta?",
  about_faq_a5: "Create a free account and join the waitlist — we review applications and send invites as space opens. Early supporters are permanently marked as Founders in the player community.",
  about_faq_q6: "Is Adventure Venture free to use?",
  about_faq_a6: "Adventure Venture offers a free tier for exploring the platform and running short campaigns. Full BYOK mode — where you supply your own API keys — often costs less than a platform subscription for regular players, since you pay your AI provider at standard rates rather than a marked-up bundle. There is no credit meter for BYOK text generation.",

  explore_byok_title: "Bring Your Own Key",
  explore_byok_desc: "Use your own API keys for text, voice & search — and enjoy unlimited gameplay for a flat monthly fee. Only Wiki & Fandom searches cost credits.",
  explore_byok_cta: "Learn More",
  explore_byok_feature_1: "Unlimited text generation with your Gemini key",
  explore_byok_feature_2: "Your own ElevenLabs & Inworld voices",
  explore_byok_feature_3: "Per-agent AI model selection",
  explore_byok_feature_4: "SERP search with your own key",
  pricing_byok_title: "Bring Your Own Key",
  pricing_byok_desc: "For power users who want full control. Plug in your own API keys and play without limits.",
  pricing_byok_price: "$20",
  pricing_byok_per_month: "/month",
  pricing_byok_feature_1: "Unlimited text generation (your Gemini key)",
  pricing_byok_feature_2: "Unlimited TTS (your ElevenLabs / Inworld key)",
  pricing_byok_feature_3: "Unlimited SERP search (your key)",
  pricing_byok_feature_4: "Per-agent model selection (advanced)",
  pricing_byok_feature_5: "Wiki & Fandom still use platform credits",
  pricing_byok_note: "You provide your own API keys. Only Wiki & Fandom searches count against credits.",
  pricing_byok_cta: "Get Started",

  // ── Explore page ──
  explore_hero_title_1: "Build",
  explore_hero_title_2: "Any Game",
  explore_hero_desc: "A text-based game engine powered by AI. Create RPGs, strategy games, tycoon sims, lore-rich adventures — or something entirely new. Build the world, the AI runs it.",
  explore_cta_start: "Join the Waitlist",
  explore_cta_dashboard: "Go to Dashboard",
  explore_cta_browse: "Browse Worlds",
  explore_how_compare: "How We Compare",
  explore_how_compare_desc: "Most AI text adventure tools — AI Dungeon, NovelAI, FableAI, Talefy — offer basic storytelling without persistent NPC memory, real game mechanics, or multiplayer. We built a full game engine with all three.",
  explore_feature: "Feature",
  explore_show_more: "More Features",
  explore_show_less: "Show Less",
  explore_comparison_note: "Comparison based on publicly available features as of March 2026. \"Partial\" means limited or basic implementation.",
  explore_what_create: "What Will You Create?",
  explore_what_create_desc: "From crime empires to enchanted academies — here's a taste of what's possible.",
  explore_mechanics_title: "Gameplay Mechanics",
  explore_mechanics_desc: "Mix and match these systems to create any genre. Each mechanic is a building block — combine them for unique gameplay.",
  explore_alpha_title: "Closed Beta — You're Early.",
  explore_alpha_desc: "You're getting access to Adventure Venture while we're actively building it — features ship weekly, and founding members directly shape what gets built. Your feedback on what works, what's missing, and what's broken has a real impact. We move fast, and early beta members are permanently marked as Founders.",
  explore_ready_title: "Ready to Build Your World?",
  explore_ready_desc: "No code required. Describe your world, configure the mechanics, and let the AI bring it to life. Play solo or invite friends for multiplayer adventures.",
  explore_get_started: "Join the Waitlist",

  // ── Roadmap page ──
  roadmap_badge: "Product Roadmap",
  roadmap_title: "Where We're Headed",
  roadmap_desc: "From world-building wizardry to multiplayer mayhem — here's everything we've shipped, what's cooking, and what's on the horizon.",
  roadmap_features: "features",
  roadmap_cta_title: "Have a Feature Request?",
  roadmap_cta_desc: "We build what the community wants. Share your ideas on the forums and vote on what ships next.",
  roadmap_cta_forums: "Visit Forums",
  roadmap_cta_explore: "Explore Worlds",
  roadmap_status_live: "Live",
  roadmap_status_beta: "Beta",
  roadmap_status_alpha: "Alpha",
  roadmap_status_planned: "Planned",
  roadmap_status_exploring: "Exploring",
  roadmap_item_world_builder: "AI World Builder Agent",
  roadmap_item_world_builder_desc: "Conversational AI assistant that creates entire worlds — NPCs, locations, factions, plot hooks, and mechanics — from natural language descriptions.",
  roadmap_item_mechanics: "30+ Game Mechanics",
  roadmap_item_mechanics_desc: "Combat, inventory, economy, reputation, morality, survival, magic, territory control, organizations, and more — all toggleable per world.",
  roadmap_item_narrator: "AI Narrator & Story Engine",
  roadmap_item_narrator_desc: "Multi-agent story orchestra with difficulty director, consequence engine, and encounter pacing for dynamic, adaptive narratives.",
  roadmap_item_image_gen: "AI Image Generation",
  roadmap_item_image_gen_desc: "Generate character portraits, scene art, and location images with web reference search, style references, and codex-aware prompts.",
  roadmap_item_tts: "Text-to-Speech Narration",
  roadmap_item_tts_desc: "Multiple voice actors with configurable voices per NPC. Immersive audio narration for every scene.",
  roadmap_item_document: "Document Upload & Analysis",
  roadmap_item_document_desc: "Upload .txt, .md, .json files to seed worlds. Multi-pass codex extraction, RAG indexing, and entity generation.",
  roadmap_item_journal: "Journal & Story Export",
  roadmap_item_journal_desc: "Generate diary entries and prose narratives from your adventure. Export as Markdown files to remember every journey.",
  roadmap_item_npc_agents: "Multi-Agent NPCs",
  roadmap_item_npc_agents_desc: "Each NPC has its own personality, memories, knowledge, and emotional state. They remember what you've done and react accordingly.",
  roadmap_item_quest_journal: "Quest Journal & Tracking",
  roadmap_item_quest_journal_desc: "Dynamic quest discovery, acceptance, progression, and completion. Main quests, side quests, and hidden objectives.",
  roadmap_item_fan_fiction: "Online Research Tool",
  roadmap_item_fan_fiction_desc: "Import reference material from the web — the AI absorbs lore, facts, and world rules from Google, Wikipedia, and Fandom to enrich your stories.",
  roadmap_item_custom_rules: "Custom Rules Engine",
  roadmap_item_custom_rules_desc: "Define your own game rules in plain text. The AI enforces them alongside built-in mechanics for fully custom gameplay.",
  roadmap_item_forums: "Community Forums & DMs",
  roadmap_item_forums_desc: "Share worlds, discuss strategies, and chat with other players. Friend system with direct messaging.",
  roadmap_item_research: "Research Module",
  roadmap_item_research_desc: "Built-in Google Search, Wikipedia, and Fandom wiki integration. Auto-import lore snippets, reference images, and canonical facts directly into your world's knowledge base.",
  roadmap_item_multiplayer: "Multiplayer Co-op",
  roadmap_item_multiplayer_desc: "Play with friends in shared adventures. Round-robin turns, initiative order, or free-form — with party management and shared quests.",
  roadmap_item_world_map: "Interactive World Map",
  roadmap_item_world_map_desc: "Visual map editor with placeable nodes, sub-maps, layers, and location linking. Navigate worlds spatially during gameplay.",
  roadmap_item_map_library: "Map Image Library",
  roadmap_item_map_library_desc: "Upload custom map images, trace SVG overlays, and create multi-variant map views with different visual styles.",
  roadmap_item_byoai: "Bring Your Own AI (18+)",
  roadmap_item_byoai_desc: "Connect your own Gemini API key for uncensored, unfiltered AI experiences. Full creative freedom with your own model and billing.",
  roadmap_item_music: "Music & Ambience System",
  roadmap_item_music_desc: "Upload ambient tracks, assign to scenes or world-wide. Background music that adapts to the mood of your adventure.",
  roadmap_item_mobile: "Mobile App",
  roadmap_item_mobile_desc: "Native mobile experience for playing adventures on the go. Optimized touch UI, push notifications, and offline reading.",
  roadmap_item_marketplace: "World Marketplace",
  roadmap_item_marketplace_desc: "Sell your worlds and scenarios to other players. Set ticket costs, clone prices, or offer free community editions.",
  roadmap_item_voice_chat: "AI Dungeon Master Voice Chat",
  roadmap_item_voice_chat_desc: "Real-time voice conversations with the AI narrator. Speak your actions and hear the story unfold.",
  roadmap_item_campaign: "Campaign Mode",
  roadmap_item_campaign_desc: "Multi-session campaigns with persistent save states, chapter progression, and branching narrative arcs.",
  roadmap_item_pdf_export: "PDF & eBook Export",
  roadmap_item_pdf_export_desc: "Export your complete adventure as a formatted PDF book or eBook — with chapter divisions, character art, and maps.",
  roadmap_item_streaming: "Streaming Integration",
  roadmap_item_streaming_desc: "OBS overlays, Twitch chat integration, and audience voting for collaborative storytelling streams.",
  roadmap_item_video: "AI-Generated Video Cutscenes",
  roadmap_item_video_desc: "Short AI-generated video clips for key story moments — boss reveals, dramatic deaths, epic victories.",
  roadmap_item_vr: "VR Integration",
  roadmap_item_vr_desc: "Step inside your world. VR-ready text rendering, spatial audio, and immersive map exploration.",
  roadmap_item_procgen: "Procedural World Generation",
  roadmap_item_procgen_desc: "One-click entire world creation with AI-generated geography, politics, history, and hundreds of interconnected NPCs.",

  // ── Support Us / Pricing page ──
  support_badge: "Support the Project",
  support_title: "Fuel Your Adventures",
  support_desc: "Credits power everything — world building, AI narration, image generation, and voice. Each pack also rewards XP and an exclusive title.",
  support_best_value: "⭐ BEST VALUE",
  support_get_pack: "Get",
  support_what_buy: "What Do Credits Buy?",
  support_ai_narration: "AI Narration",
  support_ai_narration_desc: "Each message costs ~0.01–0.04 credits",
  support_world_building: "World Building",
  support_world_building_desc: "Adding entities, mechanics, plot points",
  support_image_voice: "Image & Voice",
  support_image_voice_desc: "AI art generation and text-to-speech",
  support_xp_title: "XP & Titles",
  support_xp_desc: "Every starter pack grants bonus XP and an exclusive title displayed on your profile and in the community. Higher XP also unlocks access to premium worlds that require a minimum experience level.",
  support_title_label: "Title",
  support_lifetime_badge: "Credits are valid for lifetime — no expiration, no subscription",
  support_free: "Free",
  support_pack_trial: "Trial Pack",
  support_pack_trial_desc: "Get your feet wet — enough to create your first world and run a short adventure",
  support_pack_starter: "Starter Pack",
  support_pack_starter_desc: "Build your first world and run a handful of adventures with AI narration",
  support_pack_adventurer: "Adventurer Pack",
  support_pack_adventurer_desc: "Build rich worlds, run multiple adventures, and generate dozens of images",
  support_pack_hero: "Hero Pack",
  support_pack_hero_desc: "Unlock the full potential — massive worlds, unlimited AI narration, voice acting, and premium art generation",
  support_pack_legend: "Legend Pack",
  support_pack_legend_desc: "For the truly dedicated — build an empire of worlds, share with friends, and become a founding legend of the platform",

  // ── Contact page ──
  contact_badge: "Get In Touch",
  contact_title: "Contact Us",
  contact_desc: "Have a question, suggestion, or just want to say hi? We'd love to hear from you.",
  contact_send_message: "Send a Message",
  contact_respond_time: "We typically respond within 24 hours.",
  contact_name: "Name",
  contact_name_placeholder: "Your name",
  contact_email: "Email",
  contact_email_placeholder: "your@email.com",
  contact_subject: "Subject",
  contact_subject_placeholder: "What's this about?",
  contact_message: "Message",
  contact_message_placeholder: "Tell us what's on your mind...",
  contact_send: "Send Message",
  contact_github: "GitHub",
  contact_github_desc: "Report bugs & request features",
  contact_community: "Community",
  contact_community_desc: "Join the discussion in our forums",
  contact_support_cta: "Want to support the project?",
  contact_support_cta_desc: "Check out our starter packs to get credits and exclusive titles!",
  contact_fill_required: "Please fill in all required fields",
  contact_sent_success: "Message sent! We'll get back to you soon.",
  contact_sent_error: "Failed to send message. Please try again.",

  // ── Community page ──
  community_forums: "Forums",
  community_loading: "Loading forums...",
  forum_title: "Community Forums",
  forum_col_forum: "Forum",
  forum_col_topics: "Topics",
  forum_col_posts: "Posts",
  forum_col_last_post: "Last Post",
  forum_no_categories: "No forum categories yet.",
  forum_no_posts_yet: "No posts yet",
  forum_stats_title: "Forum Statistics",
  forum_total_topics: "Total Topics",
  forum_total_posts: "Total Posts",
  forum_total_members: "Total Members",
  forum_newest_member: "Newest Member",
  forum_by: "by",
  forum_start_new_topic: "Start New Topic",
  forum_create_topic_title: "Create New Topic",
  forum_topic_title_placeholder: "Topic title...",
  forum_first_post_placeholder: "Write your first post...",
  forum_post_anonymously: "Post anonymously",
  forum_create_topic: "Create Topic",
  forum_no_topics: "No topics yet.",
  forum_no_topics_desc: "Be the first to start a discussion!",
  forum_col_topic: "Topic",
  forum_col_replies: "Replies",
  forum_col_views: "Views",
  forum_pinned_topics: "Pinned Topics",
  forum_topics: "Topics",
  forum_posts_count: "posts",
  forum_views_count: "views",
  forum_locked: "Locked",
  forum_topic_not_found: "Topic not found.",
  forum_edited: "(edited)",
  forum_unpin: "Unpin Topic",
  forum_pin: "Pin Topic",
  forum_unlock: "Unlock Topic",
  forum_lock: "Lock Topic",
  forum_save: "Save",
  forum_cancel: "Cancel",
  forum_quote: "Quote",
  forum_reply_placeholder: "Write a reply...",
  forum_post_reply: "Post Reply",
  forum_topic_locked: "This topic is locked and no longer accepting replies.",
  forum_joined: "Joined",
  forum_member_veteran: "Veteran",
  forum_member_regular: "Regular",
  forum_member_active: "Active",
  forum_member_member: "Member",
  forum_member_newcomer: "Newcomer",
  forum_time_just_now: "just now",
  forum_time_minutes_ago: "m ago",
  forum_time_hours_ago: "h ago",
  forum_time_days_ago: "d ago",
  forum_post_updated: "Post updated!",
  forum_update_failed: "Failed to update: {error}",
  forum_action_failed: "Failed: {error}",
  forum_react_failed: "Failed to react",

  // ── Not Found page ──
  notfound_title: "404",
  notfound_desc: "Oops! Page not found",
  notfound_back: "Return to Home",

  // ── Gameplay UI ──
  play_anonymous: "Anonymous",
  play_you_suffix: "(you)",
  play_turn_label: "Turn",
  play_invite_copied: "Invite link copied!",
  play_present: "Present",
  play_scene_tags: "Scene Tags",
  play_day: "Day",
  play_time: "Time",
  play_mode: "Mode",
  play_combat: "Combat",
  play_phase: "Phase",
  play_initiative_order: "Initiative Order",
  play_player_character: "Player Character",
  play_hp: "HP",
  play_level: "Level",
  play_gold: "Gold",
  play_no_character: "No character data yet",
  play_character: "Character",
  play_world: "World",
  play_journal: "Journal",
  play_director: "Director",
  play_party_chat: "Party Chat",
  play_no_messages: "No messages yet. Say hi to your party!",
  play_message_party: "Message your party...",
  play_send_failed: "Failed to send message",
  play_portrait: "Portrait",
  play_conditions: "Conditions",
  play_inventory_empty: "Inventory is empty",
  play_weight: "Weight",
  play_encumbered: "Encumbered",
  play_heavy_load: "Heavy Load",
  play_overburdened: "Overburdened",
  play_base: "Base",
  play_active_bonuses: "Active Bonuses",
  play_equipped: "Equipped",
  play_available: "available",
  
  play_complete: "complete",
  play_failed: "failed",
  play_create_quest: "Create Quest",
  play_quest_goal: "Describe your quest goal...",
  play_go: "Go",
  play_no_quests: "No {tab} quests",
  play_accept: "Accept",
  play_rewards: "Rewards",
  play_personal: "personal",
  play_diary: "Diary",
  play_story: "Story",
  play_no_adventure: "No adventure loaded",
  play_no_entries: "No entries yet",
  play_write_hint: "Write a diary entry or generate a story",
  play_story_caught_up: "Story caught up — play more or rewrite.",
  play_story_style_placeholder: "e.g. Use short, punchy sentences. Avoid flowery language. Heavy on dialogue.",
  play_action_details: "Details...",
  play_critical: "CRITICAL!",
  play_fumble: "FUMBLE!",
  play_lore_codex: "Lore Codex",
  play_search_lore: "Search lore…",
  play_edit_world: "Edit World",
  play_edit_template: "Edit Template",
  play_edit_game: "Edit Game",
  play_editing_game_banner: "Editing Game Instance",
  play_editing_game_subtitle: "Changes only affect this game, not the template",
  play_mode_editor: "Editor",
  play_mode_play: "Play",
  play_mode_switch_aria: "Switch between Editor and Play",
  play_multiplayer: "Multiplayer",
  play_your_turn_badge: "🎯 Your turn",
  play_other_turn: "⏳ {name}'s turn",
  play_waiting_other: "Waiting for other player's turn...",
  play_hidden_messages: "{count} hidden message(s)",
  play_reclassify_fail: "Failed to reclassify message",
  play_reclassify_success: "Reclassified as {label}",
  play_continue_story: "Continue Story",
  play_rewrite_all: "Rewrite All",
  play_chapter: "Chapter",
  play_entry: "Entry",
  play_new_story: "New Story",
  play_journal_entry: "Journal Entry",
  play_read_full: "Read full",
  play_no_data: "No {label} data",

  // ── Multiplayer Communication ──
  mp_tab_chat: "Chat",
  mp_tab_board: "Board",
  mp_tab_interactions: "Interactions",
  mp_board_title: "Adventure Board",
  mp_no_threads: "No threads yet — start one!",
  mp_new_thread: "New Thread",
  mp_thread_title: "Thread title…",
  mp_write_post: "Write your post…",
  mp_create_thread: "Create Thread",
  mp_reply_placeholder: "Write a reply…",
  mp_interactions_title: "Player Interactions",
  mp_no_interactions: "No interactions yet",
  mp_new_interaction: "New Interaction",
  mp_trade: "Trade Offer",
  mp_gift: "Gift",
  mp_challenge: "Challenge",
  mp_alliance: "Alliance Request",
  mp_select_player: "Select player…",
  mp_offered_items: "Items to offer (comma-separated)",
  mp_requested_items: "Items to request (comma-separated)",
  mp_offered_gold: "Gold offered",
  mp_requested_gold: "Gold requested",
  mp_interaction_message: "Add a message…",
  mp_offering: "Offering",
  mp_requesting: "Requesting",
  mp_decline: "Decline",
  mp_not_enough_gold: "Not enough gold",
  mp_leave_adventure: "Leave Adventure",
  mp_leave_failed: "Failed to leave adventure",
  mp_left_adventure: "You left the adventure",
  mp_kick_failed: "Failed to kick player",
  mp_player_kicked: "Player kicked",
  mp_kick_confirm: "Are you sure you want to kick this player?",
  mp_kick: "Kick",
  mp_spectator: "Spectator",
  mp_you_badge: "you",
  mp_host_badge: "host",
  mp_anonymous_player: "Anonymous",
  mp_your_char_name: "Your Character Name",
  mp_waiting_for_host: "Waiting for the host to start the adventure...",
  mp_invite_code_copied: "Invite code copied!",
  mp_invite_link_copied: "Invite link copied!",
  mp_share_invite_code: "Share this invite code:",
  mp_share_invite_link: "Share this link to invite players:",
  mp_turn_free_form: "Free-form",
  mp_turn_concurrent: "Concurrent",
  mp_turn_initiative: "Initiative Order",
  mp_turn_round_robin: "Round Robin",

  // ── Builder: General Panel (About) ──
  wb_gen_about: "About",
  wb_gen_about_desc: "Identity, appearance, and info",
  wb_gen_featured_image: "Featured Image",
  wb_gen_change_banner: "Change Banner",
  wb_gen_generate_banner: "Click to generate a banner image",
  wb_gen_icon: "Icon",
  wb_gen_world_name: "World Name",
  wb_gen_world_name_placeholder: "My World",
  wb_gen_description: "Description",
  wb_gen_description_placeholder: "A brief overview of your world — what makes it unique?",
  wb_gen_promo_title: "Promo Text Generator",
  wb_gen_promo_desc: "Auto-generate promo copy from your scenarios, characters, and quests.",
  wb_gen_promo_generate: "Generate",
  wb_gen_promo_regenerate: "Regenerate",
  wb_gen_promo_seed: "Seed Text",
  wb_gen_promo_seed_placeholder: "Provide a starting point or key points to include...",
  wb_gen_promo_style: "Style",
  wb_gen_promo_length: "Length",
  wb_gen_promo_style_professional: "Professional",
  wb_gen_promo_style_epic: "Epic",
  wb_gen_promo_style_mysterious: "Mysterious",
  wb_gen_promo_style_casual: "Casual",
  wb_gen_promo_length_short: "Short (1-2 lines)",
  wb_gen_promo_length_medium: "Medium (1-2 paragraphs)",
  wb_gen_promo_length_long: "Long (3-6 paragraphs)",
  wb_gen_promo_analyzing: "Analyzing scenarios, characters & quests…",
  wb_gen_promo_copy: "Copy",
  wb_gen_promo_use_desc: "Use as Description",
  wb_gen_genre_themes: "Genre & Themes",
  wb_gen_auto_detect: "Auto-detect",
  wb_gen_genre: "Genre",
  wb_gen_genre_placeholder: "e.g. Dark Fantasy, Sci-Fi, Steampunk",
  wb_gen_themes: "Themes",
  wb_gen_themes_placeholder: "e.g. Redemption, Power struggles, Survival",
  wb_gen_content_language: "Content Language",
  wb_gen_content_language_desc: "All AI-generated content (entities, plots, codex, narration) will be written in this language.",
  wb_gen_tags: "Tags",
  wb_gen_add_tag: "Add a tag...",
  wb_gen_add: "Add",
  wb_gen_translations: "Translations",
  wb_gen_translate_now: "Translate Now",
  wb_gen_auto_translate: "Auto-Translate on Save",
  wb_gen_auto_translate_desc: "Automatically translate name, description & tags when saving changes",
  wb_gen_primary_language: "Primary language",
  wb_gen_info: "Info",
  wb_gen_world_id: "World ID",
  wb_gen_created_by: "Created by",
  wb_gen_created: "Created",
  wb_gen_saving: "Saving…",
  wb_gen_save_changes: "Save Changes",
  wb_gen_saved: "Saved",
  wb_gen_name_label: "Name",

  // ── Builder: General Panel (Generation) ──
  wb_gen_world_generation: "World Generation",
  wb_gen_world_generation_desc: "Controls content auto-generation intensity",
  wb_gen_level_conservative: "Conservative",
  wb_gen_level_balanced: "Balanced",
  wb_gen_level_aggressive: "Aggressive",
  wb_gen_level_active: "Active",
  wb_gen_auditor: "Continuity Auditor",
  wb_gen_auditor_desc: "An AI agent that periodically reviews the story for inconsistencies, plot holes, and boundary violations — then auto-corrects by injecting rules and patching entities.",
  wb_gen_enable_auditor: "Enable Auditor",
  wb_gen_auditor_runs: "Runs every N turns to validate narrative coherence",
  wb_gen_audit_interval: "Audit interval (turns)",
  wb_gen_audit_frequent: "5 (frequent)",
  wb_gen_audit_rare: "30 (rare)",

  // ── Builder: General Panel (Privacy) ──
  wb_gen_privacy: "Privacy & Sharing",
  wb_gen_privacy_desc: "Control who can see and access your world",
  wb_gen_under_dev: "Under Development",
  wb_gen_under_dev_desc: "Players will see a work-in-progress notice.",
  wb_gen_public_world: "Public World",
  wb_gen_public_world_desc: "Anyone can discover and view this world",
  wb_gen_invite_only: "Invite Only",
  wb_gen_invite_only_desc: "Players need an invitation to join",
  wb_gen_allow_cloning: "Allow Cloning",
  wb_gen_allow_cloning_desc: "Others can duplicate this world as a template",
  wb_gen_pricing: "Pricing & Credits",
  wb_gen_pricing_desc: "Set costs for players to play or clone your world. Set to 0 for free.",
  wb_gen_ticket_cost: "Ticket Cost (per session)",
  wb_gen_ticket_cost_desc: "Credits charged to start a new adventure",
  wb_gen_clone_price: "Clone Price",
  wb_gen_clone_price_desc: "Credits charged to duplicate this world",
  wb_gen_min_xp: "Minimum XP Required",
  wb_gen_min_xp_desc: "Players need this much XP to access this world (0 = no restriction)",
  wb_gen_free_for_players: "Free for Players",
  wb_gen_free_for_players_desc: "You absorb all gameplay credit costs. Players play for free.",

  // ── Builder: General Panel (Actions) ──
  wb_gen_actions: "Actions",
  wb_gen_actions_desc: "Clone or delete this world",
  wb_gen_clone_world: "Clone This World",
  wb_gen_delete_world: "Delete World",
  wb_gen_clone_dialog_title: "Clone World",
  wb_gen_clone_dialog_desc: "Creates a full copy including all entities, plots, agents, scenarios, checklists, and music tracks.",
  wb_gen_new_world_name: "New World Name",
  wb_gen_clone_btn: "Clone",
  wb_gen_delete_dialog_title: "Delete World",
  wb_gen_delete_dialog_desc: "This will permanently delete \"{name}\" and all its entities, plots, agents, scenarios, and media.",
  wb_gen_delete_cannot_undo: "This action cannot be undone.",
  wb_gen_delete_permanently: "Delete Permanently",

  // ── Builder: Entities Panel ──
  wb_ent_no_entities: "No entities yet",
  wb_ent_no_entities_desc: "Describe your world in the chat and the AI will create entities automatically.",
  wb_ent_search: "Search entities...",
  wb_ent_no_match: "No entities match this view.",

  // ── Builder: Plot Panel ──
  wb_plot_quests: "Quests",
  wb_plot_story: "Story",
  wb_plot_conditions: "Conditions",
  wb_plot_quests_desc: "Main and side quests with objectives and scenario links.",
  wb_plot_story_desc: "Key moments, events, plot hooks, conflicts, and lore.",
  wb_plot_conditions_desc: "Victory, defeat, and ending conditions.",
  wb_plot_objectives: "Objectives",
  wb_plot_add_objective: "Add objective...",
  wb_plot_relevance_critical: "Critical",
  wb_plot_relevance_high: "High",
  wb_plot_relevance_medium: "Medium",
  wb_plot_relevance_low: "Low",
  wb_plot_relevance_background: "Background",
  wb_plot_new_entry: "New Entry",
  wb_plot_no_entries: "No entries yet.",

  // ── Builder: Scenarios Panel ──
  wb_sc_desc: "Define starting scenarios — templates that shape how adventures begin.",
  wb_sc_new: "New Scenario",
  wb_sc_no_scenarios: "No scenarios yet.",
  wb_sc_no_scenarios_desc: "Create scenarios to give players structured starting points. Without scenarios, adventures start free-form.",
  wb_sc_create_first: "Create First Scenario",
  wb_sc_default: "Default",
  wb_sc_set_default: "Set as Default",
  wb_sc_title: "Title",
  wb_sc_description: "Description",
  wb_sc_opening: "Opening Narration",
  wb_sc_starting_location: "Starting Location",
  wb_sc_difficulty: "Difficulty",
  wb_sc_allow_custom: "Allow Custom Characters",
  wb_sc_guidelines: "Custom Character Guidelines",
  wb_sc_progressions: "Story Progressions",
  wb_sc_add_progression: "Add Progression",
  wb_sc_characters: "Available Characters",
  wb_sc_tags: "Tags",
  wb_sc_needs_title: "Scenario needs a title",

  // ── Builder: Players Panel ──
  wb_pc_title: "Player Characters",
  wb_pc_desc: "Pre-built characters that players can choose when starting an adventure.",
  wb_pc_new: "New Character",
  wb_pc_no_characters: "No player characters yet.",
  wb_pc_no_characters_desc: "Create characters for players to choose from, or let them create their own.",
  wb_pc_create_first: "Create First Character",
  wb_pc_identity: "Identity",
  wb_pc_backstory: "Backstory",
  wb_pc_personality: "Personality",
  wb_pc_stats: "Stats",
  wb_pc_inventory: "Inventory",

  // ── Builder: Mechanics Panel ──
  wb_mech_search: "Search mechanics...",
  wb_mech_allow_dynamic: "Allow Dynamic Mechanics",
  wb_mech_allow_dynamic_desc: "Let the AI add/remove mechanics during gameplay",
  wb_mech_active: "active",
  wb_mech_guidance_placeholder: "Describe how this mechanic manifests in your world — who controls it, where it's accessed, what's required narratively.",

  // ── Builder: Agent Chat Dialog ──
  wb_agent_select_npc: "Select NPC…",
  wb_agent_describe_self: "Describe who you are…",
  wb_agent_select_location: "Select location…",
  wb_agent_location_example: "A dimly lit tavern…",
  wb_agent_time_example: "Late evening…",
  wb_agent_context_example: "What just happened, mood…",

  // ── Builder: Agent Editor ──
  wb_agent_no_linked_entity: "No linked entity",
  wb_agent_traits_placeholder: "brave, cunning, kind...",
  wb_agent_values_placeholder: "honor, freedom, knowledge...",
  wb_agent_fears_placeholder: "abandonment, fire...",
  wb_agent_goals_placeholder: "find the artifact, protect the village...",
  wb_agent_speech_patterns_placeholder: "speaks in riddles, uses metaphors...",
  wb_agent_verbal_tics_placeholder: "'indeed', clears throat, sighs...",
  wb_agent_use_world_default: "Use world default",
  wb_agent_use_default_model: "Use default model",

  // ── Builder: Checklist Panel ──
  wb_checklist_task_name: "e.g. Create magic system",
  wb_checklist_task_desc: "Brief description",
  wb_checklist_task_prompt: "The message sent to the AI when clicked...",

  // ── Builder: Context Panel ──
  wb_context_desc_placeholder: "Add a world description...",

  // ── Builder: Custom Rules Editor ──
  wb_rule_title_placeholder: "e.g. Language Barrier",
  wb_rule_text_placeholder: "Describe the rule the AI should follow...",

  // ── Builder: Entities Panel ──
  wb_ent_search_placeholder: "Search entities...",

  // ── Builder: Entity Detail Dialog ──
  wb_ent_add_tag: "add_tag...",
  wb_ent_json_data: "JSON data",
  wb_ent_refine_instructions: "e.g. Focus on military history, add more dialogue examples...",
  wb_ent_name_placeholder: "Entity name",
  wb_ent_tag_input: "Add tag...",
  wb_ent_desc_detail: "Describe the entity in detail...",

  // ── Builder: Entity Property Helpers ──
  wb_ent_custom_field: "custom_field",

  // ── Builder: General Panel ──
  wb_gen_promo_section_placeholder: "Promotional text for this section… Leave empty to show scenarios instead.",

  // ── Builder: Group Chat Dialog ──
  wb_group_location_placeholder: "Tavern, throne room...",
  wb_group_time_placeholder: "Evening, after the battle...",

  // ── Builder: Initial Threads Editor ──
  wb_thread_desc_placeholder: "What is this narrative thread about? E.g. 'A merchant caravan has gone missing on the northern road...'",
  wb_thread_hook_placeholder: "How this could first appear in the story...",

  // ── Builder: Player Characters Panel ──
  wb_pc_backstory_placeholder: "Enter backstory as text or paste structured JSON...",
  wb_pc_name_placeholder: "Aldric the Brave",
  wb_pc_class_placeholder: "Warrior, Mage, Rogue...",
  wb_pc_desc_placeholder: "A wandering knight seeking redemption...",
  wb_pc_playstyle_placeholder: "Aggressive, diplomatic, stealthy...",

  // ── Builder: Relationship Editor ──
  wb_rel_search_entities: "Search entities...",
  wb_rel_custom_name: "Or type custom name...",
  wb_rel_type_search: "Type or search...",
  wb_rel_custom_type: "Custom type...",
  wb_rel_desc: "Description (optional)",
  wb_rel_notes: "Notes (optional)",

  // ── Builder: Scenarios Panel ──
  wb_sc_title_placeholder: "The Dark Forest Awakens...",
  wb_sc_desc_placeholder: "A brief overview players will see when selecting...",
  wb_sc_opening_placeholder: "The text players read when the adventure begins...",
  wb_sc_location_placeholder: "The Misty Tavern",
  wb_sc_guidelines_placeholder: "e.g. Characters must be human, no magic users...",
  wb_sc_path_name: "Path name",
  wb_sc_path_desc: "Description",
  wb_sc_item_name: "Item name",
  wb_sc_item_qty: "Qty",
  wb_sc_item_category: "Category",
  wb_sc_item_rarity: "Rarity",
  wb_sc_tags_placeholder: "mystery, combat-heavy, exploration...",

  // ── Builder: Share World Dialog ──
  wb_share_email_placeholder: "User email...",

  // ── Builder: Source References Panel ──
  wb_src_title_placeholder: "Title (e.g. Star Wars Fandom Wiki)",
  wb_src_url_placeholder: "URL (optional)",
  wb_src_desc_placeholder: "Short description",

  // ── Builder: Style References Panel ──
  wb_style_label_placeholder: "e.g. Location style",

  // ── Builder: Template Selector ──
  wb_template_search: "Search templates...",

  // ── Builder: Voice Controls Panel ──
  wb_voice_select_placeholder: "Select voice...",
  wb_voice_id_placeholder_venice: "e.g. af_sky",
  wb_voice_id_placeholder_elevenlabs: "e.g. JBFqnCBsd6RMkjVDRZzb",
  wb_voice_sample_placeholder: "Short sample to preview this voice",
  wb_voice_describe_placeholder: "Describe a voice... deep male, soft female, mysterious",

  // ── Builder: Art Direction Tab ──
  wb_art_direction_placeholder: "e.g. dark moody lighting, cinematic composition...",
  wb_art_brief_manual_placeholder: "Write a visual description manually...",

  // ── Index / World Stats ──
  idx_characters: "character",
  idx_scenarios: "scenario",
  idx_entities: "entity",
  idx_all_worlds: "All Worlds",
  idx_all_worlds_admin: "All Worlds (Admin)",

  // ── Header Credits ──
  hdr_credits: "credits",
  hdr_credit_wallet: "Credit Wallet",
  hdr_usage_dashboard: "Usage Dashboard",
  hdr_pricing_rates: "Pricing & Rates",
  hdr_earn_credits: "Earn Credits",
  hdr_ai_disabled: "All AI features disabled",
  hdr_building_disabled: "World building disabled (need 50+)",

  // ── Reset Password page ──
  reset_title_done: "All Set!",
  reset_title_new: "New Password",
  reset_desc_done: "Your password has been updated. Redirecting…",
  reset_desc_new: "Choose a new password for your account",
  reset_redirecting: "Redirecting to your dashboard…",
  reset_link_expired: "This link may have expired or is invalid. Please request a new password reset.",
  reset_back_sign_in: "Back to Sign In",
  reset_new_password: "New Password",
  reset_confirm_password: "Confirm Password",
  reset_placeholder: "At least 6 characters",
  reset_re_enter: "Re-enter password",
  reset_updating: "Updating…",
  reset_update_button: "Update Password",
  reset_password_min: "Password must be at least 6 characters.",
  reset_passwords_mismatch: "Passwords do not match.",
  reset_success: "Password updated successfully!",

  // ── Auth extras ──
  auth_remember_me: "Remember me",
  auth_err_invalid_creds: "Incorrect email or password. Please try again.",
  auth_err_email_not_confirmed: "Please verify your email before signing in. Check your inbox.",
  auth_err_user_exists: "An account with this email already exists. Try signing in.",
  auth_err_password_too_short: "Password must be at least 6 characters.",
  auth_err_rate_limit: "Too many attempts. Please wait a moment and try again.",
  auth_err_signup_disabled: "Sign-ups are currently disabled.",
  auth_err_generic: "An error occurred. Please try again.",

  // ── Common actions ──
  common_generating: "Generating...",
  common_processing: "Processing...",
  common_resume: "Resume",
  common_pause: "Pause",
  common_generate: "Generate",
  common_saving: "Saving...",

  // ── World Builder: various panels ──
  wb_voice_save: "Save voice settings",
  wb_agent_auto_generate: "Auto-Generate",
  wb_doc_generate_codex: "Generate Codex",
  wb_doc_status_vector: "Vector",
  wb_doc_status_text: "Text",
  wb_doc_status_error: "Error",
  wb_doc_indexed: "Indexed for search",
  wb_doc_timeout: "Upload may have timed out",
  wb_doc_still_processing: "Still processing...",
  wb_doc_error_processing: "Error during processing",
  wb_map_rename: "Rename",
  wb_map_upload_image: "Upload Map Image",
  wb_map_upload_overlay: "Upload Layer Overlay",
  wb_map_generate_ai: "Generate Map with AI",

  play_tts_disabled: "Text-to-speech ({provider}) is currently disabled for your account.",

  // ── Play: Adventure setup ──
  play_setup_char_name: "Character name...",
  play_setup_char_desc: "Brief character description (the AI will help flesh it out)...",
  play_setup_adventure_name: "Adventure name...",
  gm_ask_placeholder: "Ask the GM… (Shift+Enter for new line)",
  notes_title_placeholder: "Note title…",
  notes_content_placeholder: "Write your note…",
  notes_untitled: "Untitled",
  notes_untitled_note: "Untitled Note",

  // ── Social: Friends panel ──
  social_search_placeholder: "Search by name or email...",
  social_no_users: "No users found",
  social_search_failed: "Search failed",
  social_find_players: "Find Players",
  social_friend_requests: "Friend Requests",
  social_friends: "Contacts",
  social_no_friends: "No contacts yet. Search for players above!",
  social_sent_requests: "Sent Requests",
  wb_rename_failed: "Failed to rename world",
  wb_rename_tooltip: "Click to rename",
  msg_page_title: "Messages",
  msg_unknown_contact: "Unknown",
  chat_conversation_reset: "Conversation reset",
  chat_send_failed: "Failed to send message",
  chat_no_messages: "No messages yet. Say hello!",
  chat_type_message: "Type a message...",

  // ── Accessible button labels (aria-label) ──
  aria_send_message: "Send message",
  aria_message_options: "Message options",
  aria_edit_message: "Edit message",
  aria_delete_message: "Delete message",
  aria_roll_dice: "Roll dice",
  aria_notifications: "Notifications",
  notif_mark_all_read: "Mark all read",
  notif_empty: "No notifications",
  aria_pause_narration: "Pause narration",
  aria_resume_narration: "Resume narration",
  aria_stop_narration: "Stop narration",
  aria_replay_narration: "Replay narration",
  aria_next_track: "Skip to next track",

  // ── useMultiplayerAdventure ──
  adv_load_failed: "Failed to load adventure: {error}",
  adv_your_turn: "⚔️ It's your turn!",
  adv_your_turn_desc: "{name}'s turn to act",
  adv_create_failed: "Failed to create adventure: {error}",
  adv_invalid_invite: "Invalid invite code",
  adv_joined: "Joined adventure!",
  adv_join_failed: "Failed to join: {error}",
  adv_no_retry_msg: "No user message found to retry from",
  adv_orphaned_removed: "Orphaned message removed",
  adv_msg_delete_failed: "Failed to delete message",
  adv_msg_deleted: "Message deleted",
  adv_msg_edit_failed: "Failed to edit message",
  adv_msg_updated: "Message updated",

  // ── useFriends ──
  friends_request_exists: "Friend request already exists",
  friends_request_failed: "Failed to send friend request",
  friends_request_sent: "Friend request sent!",
  friends_accept_failed: "Failed to accept request",
  friends_request_accepted: "Friend request accepted!",
  friends_decline_failed: "Failed to decline request",
  friends_request_declined: "Friend request declined",
  friends_remove_failed: "Failed to remove friend",
  friends_removed: "Friend removed",

  // ── useIndexData (Index page actions) ──
  idx_load_failed: "Failed to load data",
  idx_file_too_large: "{name} is too large (max 2MB)",
  idx_file_read_failed: "Could not read {name}",
  idx_need_credits: "You need at least 50 credits to create a world",
  idx_create_world_failed: "Failed to create world: {error}",
  idx_world_trashed: "World moved to trash",
  idx_world_restored: "World restored",
  idx_world_perm_deleted: "World permanently deleted",
  idx_world_published: "World published!",
  idx_world_unpublished: "World unpublished",
  idx_world_cloned: "World cloned successfully!",
  idx_clone_failed: "Failed to clone: {error}",
  idx_adventure_deleted: "Adventure deleted",
  idx_adventure_left: "Left adventure",
  idx_adventure_joined: "Joined adventure!",
  idx_invalid_invite: "Invalid invite code",
  idx_invite_copied: "Invite link copied!",
  idx_delete_failed: "Failed to delete: {error}",
  idx_restore_failed: "Failed to restore: {error}",
  idx_update_failed: "Failed to update: {error}",
  idx_leave_failed: "Failed to leave: {error}",
  idx_join_failed: "Failed to join: {error}",
  idx_start_game_failed: "Failed to start game: {error}",

  // ── DocumentUpload ──
  doc_file_too_large: "{name} is too large (max 20MB)",
  doc_file_already_exists: "\"{name}\" already exists in the knowledge base",
  doc_file_empty: "{name} is empty",
  doc_file_duplicate_content: "\"{name}\" has identical content to an existing document",
  doc_upload_success: "{name} → {chunks} chunks ({category})",
  doc_upload_failed: "Failed: {error}",
  doc_no_supported_files: "No supported files. Use: {types}",
  doc_deleted: "Deleted {name}",
  doc_delete_failed: "Delete failed: {error}",
  doc_removed_stuck: "Removed stuck \"{name}\" — please re-upload the file",
  doc_reindexed: "Re-indexed {name}: {chunks} chunks",
  doc_reindex_failed: "Re-index failed: {error}",
  doc_codex_regenerated: "Codex regenerated for {name}",
  doc_codex_failed: "Codex regeneration failed: {error}",
  doc_reanalyzed: "Re-analyzed {name}: {chunks} chunks ({mode})",
  doc_reanalyze_failed: "Re-analyze failed: {error}",

  // ── ShareWorldDialog ──
  share_link_copied: "Share link copied!",
  share_copy_failed: "Failed to copy link",
  share_load_collab_failed: "Failed to load collaborators",
  share_user_not_found: "No user found with that email. They need to sign up first.",
  share_cannot_add_self: "You can't add yourself as a collaborator.",
  share_already_collab: "This user is already a collaborator.",
  share_collab_added: "Added {email} as {role} collaborator",
  share_add_collab_failed: "Failed to add collaborator: {error}",
  share_permission_updated: "Permission updated",
  share_update_failed: "Failed to update: {error}",
  share_collab_removed: "Collaborator removed",
  share_remove_failed: "Failed to remove: {error}",

  // ── useCreditBalance ──
  cred_img_disabled: "Image generation disabled",
  cred_img_disabled_desc: "Balance dropped below {threshold} credits ({balance}). Image generation is now disabled to preserve credits.",
  cred_running_low: "Credits running low",
  cred_running_low_desc: "Your balance is {balance} credits. Consider conserving usage.",
  cred_voice_disabled: "Voice generation disabled",
  cred_voice_disabled_desc: "Balance dropped below {threshold} credits ({balance}). Voice/TTS features are now disabled.",
  cred_transfer_disabled: "Credit transfer disabled",
  cred_transfer_disabled_desc: "Balance dropped below {threshold} credits ({balance}). Credit transfers are now disabled.",
  cred_build_disabled: "World building disabled",
  cred_build_disabled_desc: "Balance dropped below {threshold} credits ({balance}). World creation and AI building features are now disabled.",
  cred_all_disabled: "All credit features disabled",
  cred_all_disabled_desc: "Your credit balance has reached 0. All AI-powered features are disabled until you receive more credits.",

  // ── GeneralPanel ──
  gen_world_saved: "World details saved",
  gen_save_failed: "Failed to save: {error}",
  gen_promo_generated: "Promo text generated!",
  gen_copied_clipboard: "Copied to clipboard",
  gen_intro_applied: "Intro applied to description",
  gen_all_langs_translated: "All languages already have translations",
  gen_translations_generated: "Translations generated!",
  gen_generation_failed: "Generation failed",
  gen_translation_failed: "Translation failed",

  // ── WorldMapEditor ──
  map_layer_placed: "Layer placed on map",
  map_polygon_created: "Polygon area created",
  map_connection_added: "Connection added",
  map_placed_locations: "Placed {count} locations",
  map_failed_suffix: "({count} failed)",
  map_geocode_failed: "Could not geocode {count} locations",
  map_all_placed: "All locations already placed",
  map_cannot_resolve: "Could not resolve a modern map location for \"{name}\"",
  map_moved_via: "Moved \"{name}\" via \"{query}\"",
  map_fix_failed: "AI fix failed: {error}",
  map_variant_created: "Variant \"{name}\" created",
  map_variant_duplicated: "Variant duplicated",
  map_connection_removed: "Connection removed",
  map_added_layer: "Added as layer",
  map_add_layer_failed: "Failed to add layer",
  map_drawing_saved: "Layer drawing saved",
  map_save_drawing_failed: "Failed to save drawing: {error}",
  map_ai_layer_generated: "AI layer generated",
  map_ai_no_image: "Generation returned no image",
  map_ai_generation_failed: "AI generation failed: {error}",
  map_switched_to: "Switched to {type}",
  map_linked_to: "Map linked to {name}",
  map_unlinked: "Map unlinked from location",
  map_placed_coords: "Placed \"{label}\" at {lat}, {lng}",
  map_geocode_not_found: "Could not find \"{address}\"",
  map_geocode_error: "Geocode failed: {error}",
  map_placed_via: "Placed \"{name}\" via \"{query}\"",
  map_default_view_saved: "Default view saved ({lat}, {lng} z{zoom})",

  // ── import-utils ──
  imp_loading_world: "Importing world template…",
  imp_invalid_world_file: "Invalid export file. Expected a world template export.",
  imp_sign_in_required: "You must be signed in to import.",
  imp_world_success: "World imported! {summary}",
  imp_failed: "Import failed: {error}",
  imp_loading_game: "Importing game instance…",
  imp_invalid_game_file: "Invalid export file. Expected a game instance export.",
  imp_game_success: "Game instance imported! {messages} messages, {entities} entities",
  imp_resetting: "Resetting adventure overrides…",
  imp_reset_success: "Adventure overrides reset!",
  imp_reset_failed: "Reset failed: {error}",

  // ── export-utils ──
  exp_loading_world: "Exporting world template…",
  exp_world_success: "World template exported!",
  exp_failed: "Export failed: {error}",
  exp_loading_player: "Exporting player data…",
  exp_player_success: "Player data exported!",
  exp_loading_game: "Exporting game instance…",
  exp_game_success: "Game instance exported!",

  // ── ImageGenerationDialog ──
  img_gen_disabled: "Image generation is currently disabled for your account. Contact an administrator to enable it.",
  img_only_images: "Only image files are supported",
  img_ref_uploaded: "Reference image uploaded",
  img_upload_failed: "Upload failed: {error}",
  img_draft_loaded: "Draft loaded",
  img_draft_deleted: "Draft deleted",
  img_drawing_saved: "Drawing saved to media gallery",
  img_drawing_save_failed: "Failed to save drawing: {error}",
  img_generated: "Image generated — review and accept or try again",
  // ── useWorldBuilderAgent ──
  wb_load_failed: "Failed to load world: {error}",
  wb_create_failed: "Failed to create world: {error}",
  wb_world_saved: "World saved successfully",
  wb_save_failed: "Failed to save: {error}",
  wb_credits_required: "World building disabled — you need at least 50 credits",
  wb_agent_error: "Agent error: {error}",
  // ── World ready celebration ──
  wb_world_ready_title: "🎉 Your world is ready!",
  wb_world_ready_desc: "Explore the codex, tweak the details, or jump into an adventure.",
  // ── useTTS ──
  tts_no_voice: "No narration voice configured yet (set one in World Builder → Context)",
  tts_no_voice_short: "No narration voice configured",
  tts_downloaded: "Audio downloaded",
  tts_download_failed: "Failed to download audio",
  // ── useFeatureFlags ──
  ff_locked: "This feature is locked by an administrator.",
  ff_update_failed: "Failed to update feature flag",
  ff_lock_failed: "Failed to update lock",
  ff_update_flags_failed: "Failed to update feature flags",
  // ── StyleReferencesPanel ──
  sref_added: "Style reference added",
  sref_upload_failed: "Upload failed: {error}",
  sref_delete_failed: "Failed to delete",
  sref_removed: "Reference removed",
  // ── TrashManagerDialog ──
  trash_load_failed: "Failed to load trash: {error}",
  trash_restored: "Restored \"{name}\"",
  trash_restore_failed: "Restore failed: {error}",
  trash_deleted: "Permanently deleted \"{name}\"",
  trash_delete_failed: "Delete failed: {error}",
  trash_emptied: "Trash emptied",
  trash_empty_failed: "Failed to empty trash: {error}",
  // ── ChecklistPanel ──
  check_title_required: "Title is required",
  check_updated: "Updated",
  check_added: "Added",
  check_reset: "Checklist reset to defaults",
  check_all_done: "All items are already completed!",
  check_queued: "Queued {count} tasks for AI generation (including final review)",
  // ── ReportContentButton ──
  rep_sign_in_required: "You must be signed in to report content.",
  rep_already_reported: "You've already reported this content. Our team is reviewing it.",
  rep_submitted: "Report submitted. Thank you for helping keep AdVent safe.",
  rep_submit_failed: "Could not submit report",
  rep_try_later: "Please try again later.",
  // ── useMapVariants ──
  var_create_failed: "Failed to create variant",
  var_update_failed: "Failed to update variant",
  var_delete_last: "Cannot delete the last variant",
  var_delete_failed: "Failed to delete variant",
  var_active_failed: "Failed to set active variant",
  // ── useMapUploads ──
  upl_map_uploaded: "Map image uploaded",
  upl_upload_failed: "Upload failed",
  upl_layer_added: "Layer added",
  upl_layer_failed: "Layer upload failed",
  upl_base_assigned: "Assigned as base map",
  upl_map_applied: "Map image applied",
  upl_svg_applied: "SVG map applied",
  // ── Map editor / canvas placeholders ──
  map_node_label_placeholder: "Label (e.g. Castle Rock)",
  map_lat_placeholder: "Lat",
  map_lng_placeholder: "Lng",
  map_address_placeholder: "Address or place name...",
  map_search_nodes_placeholder: "Search nodes...",
  map_connection_desc_placeholder: "A dangerous pass...",
  map_search_nodes_places_placeholder: "Search nodes or places...",
  map_location_name_placeholder: "Location name...",
  map_voice_default_placeholder: "Default",
  map_search_locations_placeholder: "Search locations...",
  map_node_desc_placeholder: "Brief description (optional)...",
  map_name_placeholder: "World Map",
  map_all_locations_filter: "All locations (no filter)",
  map_node_image_url: "https://... or leave empty for icon",
  map_node_tags_placeholder: "e.g. Faction territory, Population...",
  map_style_json_url: "https://example.com/style.json",
  // ── useMapNodes ──
  node_create_failed: "Failed to create node",
  node_delete_failed: "Failed to delete node",
  node_save_partial: "Some changes failed to save",
  node_saved: "Map saved",
  node_save_failed: "Failed to save map: {error}",
  // ── MediaGallery ──
  media_img_generated: "Image generated!",
  media_generation_failed: "Generation failed",
  media_delete_failed: "Failed to delete",
  media_update_failed: "Failed to update",
  media_set_main: "Set as main image",
  media_disconnect_failed: "Failed to disconnect",
  media_archived: "Image archived (disconnected from entity)",
  // ── EntityDetailDialog ──
  ent_updated: "Entity updated",
  ent_save_failed: "Save failed: {error}",
  ent_trashed: '"{name}" moved to trash',
  ent_delete_failed: "Delete failed: {error}",
  ent_id_copied: "Entity ID copied",
  ent_deactivated: "{name} deactivated",
  ent_activated: "{name} activated",
  ent_dormant: "{name} is now dormant",
  ent_active: "{name} is now active",
  // ── CreditWalletPanel ──
  cw_invalid_amount: "Enter a valid amount",
  cw_balance_low: "Credit transfers are disabled when your balance is below 100",
  cw_min_transfer: "Minimum transfer amount is 100 credits",
  cw_insufficient: "Insufficient credits",
  cw_sent: "Sent {amount} credits!",
  // ── AgentEditor / AgentChatDialog / GroupChatDialog ──
  agent_needs_name: "Agent needs a name",
  agent_saved: "Agent saved",
  agent_deleted: "Agent deleted",
  agent_chat_failed: "Chat failed: {error}",
  agent_group_failed: "Group chat failed: {error}",
  // ── World builder misc components ──
  ctx_desc_update_failed: "Failed to update description",
  guidance_save_failed: "Failed to save: {error}",
  guidance_saved: "Image guidance saved",
  banner_save_failed: "Failed to save banner: {error}",
  banner_removed: "Banner removed",
  banner_remove_failed: "Failed to remove banner: {error}",
  icon_save_failed: "Failed to save icon: {error}",
  icon_removed: "Icon removed",
  icon_remove_failed: "Failed to remove icon: {error}",
  src_ref_add_failed: "Failed to add reference",
  src_ref_deleted: "Reference deleted",
  src_ref_update_failed: "Failed to update",
  pc_updated: "Character updated",
  pc_created: "Character created",
  pc_needs_name: "Character needs a name first",
  adv_inst_save_failed: "Failed to save changes",
  auto_gen_complete: "World generation complete!",
  voice_saved: "Voice settings saved",
  gen_themes_generated: "Genre & themes generated!",
  // ── useWorldMaps / useMapEntityActions ──
  wmap_create_failed: "Failed to create map",
  wmap_update_failed: "Failed to update map",
  wmap_delete_failed: "Failed to delete map",
  mea_create_failed: "Failed to create location: {error}",
  mea_gen_failed: "Failed to generate: {error}",
  wh_download_failed: "Download failed",
  // ── Play components ──
  play_msg_updated: "Message updated",
  play_snapshot_downloaded: "Game snapshot downloaded",
  play_snapshot_failed: "Failed to build snapshot",
  play_downloaded: "Downloaded",
  // ── Map components ──
  map_lib_added: "Map image added to library",
  map_lib_removed: "Removed from library",
  svg_traced: "SVG traced successfully",
  svg_lib_saved: "SVG saved to library",
  // ── MusicLibraryPanel / imagegen ──
  music_not_auth: "Not authenticated",
  music_group_update_failed: "Failed to update group",
  draft_started: "Started new draft",
  draw_seed_saved: "Drawing saved as seed & to media gallery",
  // ── Settings / Profile / Layout ──
  byok_saved: "BYOK settings saved",
  ai_enter_key: "Please enter an API key first",
  app_bg_reset: "Reset to default background",
  custom_ai_saved: "Custom AI settings saved!",
  custom_ai_save_failed: "Failed to save: {error}",
  custom_ai_name_placeholder: "Custom AI",
  custom_ai_model_placeholder: "e.g. gpt-4o, mistral-large, etc.",
  custom_ai_endpoint_placeholder: "https://your-endpoint.com/v1/chat/completions",
  custom_ai_key_placeholder: "Enter API key",
  custom_ai_instructions_placeholder: "Custom instructions for {label}…",
  settings_activity_filter_user: "Filter by user_id (optional UUID)…",
  settings_activity_search: "Search logs...",
  settings_credit_email: "user@example.com",
  settings_error_search: "Search errors...",
  settings_tool_search_query: "Enter a search query...",
  settings_tool_fetch_url: "URL to fetch through proxy",
  settings_tool_franchise_slug: "Franchise slug (e.g. lotr, starwars, fallout)",
  mod_notes_placeholder: "Internal notes about this decision (visible to admins only).",
  mod_report_details: "Add anything that helps us understand the issue.",
  byok_api_key_placeholder: "Paste your API key…",
  byok_account_name_placeholder: "Account name",
  byok_use_default: "Use default",
  byok_endpoint_url: "https://api.openai.com/v1/chat/completions",
  ref_link_copied: "Referral link copied!",
  copy_failed: "Failed to copy",
  sign_out_failed: "Error signing out",
  admin_search_users: "Search by name, email, or ID...",
  // ── Community ──
  forum_topic_created: "Topic created!",
  forum_topic_failed: "Failed: {error}",
};
